import { useEffect, useEffectEvent, useRef, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { AutoFloatingPanel } from 'src/designSystem/AutoFloatingPanel';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { createCdnIcon } from 'src/designSystem/CdnIcon';
import PanelHeader from 'src/designSystem/PanelHeader';
import RechargeFreezeButton from 'src/components/Streak/freeze/RechargeFreezeButton';
import StreakFreezeSlots from 'src/components/Streak/freeze/StreakFreezeSlots';
import { useBuyStreakFreeze } from 'src/components/Streak/freeze/hooks/useBuyStreakFreeze';
import type { StreakFreezes } from 'src/types/streak.types';

const FreezeProtectedIcon = createCdnIcon('streak/freeze-protected.svg');

type Props = {
  isOpen: boolean;
  onClose: () => void;
  saved: { days_covered: number } | null;
  streakLength: number;
  freezesLeft: number;
  freezes?: StreakFreezes;
};
export type { Props as StreakProtectedPanelProps };

type StreakProtectedRechargeProps = {
  freezes: StreakFreezes;
  held: number;
};

const StreakProtectedRecharge: FunctionComponent<
  StreakProtectedRechargeProps
> = ({ freezes, held }) => {
  const { t } = useTranslation();
  const {
    state,
    buy,
    price,
    currency,
    isLoading,
    isError,
    isRejectionExplained,
    isBalanceUnavailable,
  } = useBuyStreakFreeze({ freezes, held, source: 'streak_saved' });

  return (
    <>
      {((isError && !isRejectionExplained) || isBalanceUnavailable) && (
        <span
          data-testid="streak-freeze-purchase-error"
          className="text-balance text-center text-base font-semibold text-red-neutral"
        >
          {t('streak.freeze.purchase.error')}
        </span>
      )}
      <RechargeFreezeButton
        price={price}
        currency={currency}
        onClick={buy}
        disabled={state !== 'confirm'}
        loading={isLoading}
        data-testid="btn-streak-protected-recharge"
      />
    </>
  );
};

const StreakProtectedPanel: FunctionComponent<Props> = ({
  isOpen,
  onClose,
  saved,
  streakLength,
  freezesLeft,
  freezes,
}) => {
  const { t } = useTranslation();
  const prevOpenRef = useRef(false);

  const logViewed = useEffectEvent(() => {
    if (!saved) return;
    void logAnalyticsEvent('streak_protected_viewed', {
      days_covered: saved.days_covered,
      freezes_left: freezesLeft,
    });
  });

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      logViewed();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <AutoFloatingPanel
      isOpen={isOpen}
      onClose={onClose}
      data-testid="streak-protected-panel"
    >
      {saved && isOpen && (
        <>
          <PanelHeader
            illustration={
              <FreezeProtectedIcon
                data-testid="streak-protected-cube"
                className="h-[164px] w-auto aspect-[26/33]"
              />
            }
            title={
              <span data-testid="streak-protected-title">
                {t('streak.freeze.protected.title')}
              </span>
            }
            description={t('streak.freeze.protected.body', {
              count: saved.days_covered,
              days: streakLength,
            })}
          />
          <div className="flex w-full flex-col items-center gap-2.5">
            {freezes && (
              <StreakFreezeSlots
                held={freezesLeft}
                cap={freezes.cap}
                size="large"
              />
            )}
            <span
              data-testid="streak-protected-left"
              className="text-center text-base font-semibold text-structural-darker"
            >
              {t('streak.freeze.protected.left', { count: freezesLeft })}
            </span>
          </div>
          {freezes && (
            <StreakProtectedRecharge freezes={freezes} held={freezesLeft} />
          )}
        </>
      )}
    </AutoFloatingPanel>
  );
};

export default StreakProtectedPanel;
